import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash-es';
import { MethodSettlementService } from './method-settlement.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

interface Settlement {
  type: any;
  buc: string;
  adn: string;
  amount: string | number;
  erp: any;
  company_code: any;
  me: string;
  leid: string;
  sys_desc: string;
  cCodeSet: boolean;
  foreign_curr: string;
  valid?: boolean | string;
}

@Component({
    selector: 'gtw-method-settlement',
    templateUrl: './method-settlement.component.html',
    styleUrls: ['./method-settlement.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MethodSettlementComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  private isLoading = false;

  baseURLs: any = {};
  filterParams: any = {};
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;
  rowData: any = {};

  title = "Method Of Settlement";
  crudLoading = false;
  settlements: Settlement[] = [];
  amountToSettle: number = 0;
  settleAmount: string = '0.00';
  loading = false;
  minimise = false;
  isSaveClicked = false;
  showFormLoader = true;

  companyCodes: any[] = [];
  loadingCompanyCodes = false;
  noResults = false;

  types = [
    { value: 'IBS', label: 'IBS' },
    { value: 'ACA', label: 'ACA' }
  ];

  erp = [
    { value: 'CCL', label: 'CCL' },
    { value: 'NAV', label: 'NAV' },
    { value: 'ORA', label: 'ORA' },
    { value: 'Other', label: 'Other' }
  ];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private methodSettlementService: MethodSettlementService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header;
    this.appService.USER_SETTINGS = header.userSettingsData;
    this.appService.SERVER_CONFIG = header.serverConfigData;
    this.GENERAL_CONFIG = header.generalConfigData;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  // Gate function to prevent duplicate initialization
  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init() {
    try {
      // Check if locked
      if (this.rowData.Is_Locked_0 && this.rowData.Is_Locked_0 === 1) {
        this._snackBar.open('This LEID / Division combination is locked', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
        setTimeout(() => this.cancel(), 30);
        return;
      }

      this.amountToSettle = this.rowData.Settlement__Outflow__Inflow_0 ? this.rowData.Settlement__Outflow__Inflow_0 : 0;
      this.getSettlement();
    } catch (e) {
      console.error('Error in init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  getSettlement() {
    this.loading = true;
    this.showFormLoader = true;

    const params = {
      leid: this.rowData.LEID,
      division: this.rowData.Division,
      object_id: this.rowData.object_id
    };

    this.methodSettlementService.getSettlementArray(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.loading = false;
        this.showFormLoader = false;

        if (result.callSuccess !== '1') {
          this._snackBar.open(result.errorMessage || 'Error loading data', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          if (result.jsonObject && result.jsonObject.length > 0) {
            this.setSettlement(result.jsonObject);
            this.setSettleAmount();
          } else {
            this.addSettlement();
            this.setSettleAmount();
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.loading = false;
        this.showFormLoader = false;
        console.error('Error loading settlement:', error);
        this._snackBar.open('Error loading data', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  setSettlement(array: any[]) {
    array.forEach((value: any) => {
      const settlement: Settlement = {
        type: _.find(this.types, ['value', value.SETTLEMENT_TYPE]) || null,
        buc: value.BUC_CODE || '',
        adn: value.ADN_CODE || '',
        company_code: value.COMPANY ? { NAME: value.COMPANY } : '',
        erp: _.find(this.erp, ['value', value.ERP]) || null,
        foreign_curr: value.FOREIGN_CURR || '',
        me: value.ME_CODE || '',
        leid: value.LE_CODE || '',
        amount: value.SETTLEMENT_AMT,
        sys_desc: value.SYS_DESC || '',
        cCodeSet: false
      };

      if (settlement.erp && settlement.erp.value === 'CCL') {
        settlement.cCodeSet = true;
      }

      this.settlements.push(_.clone(settlement));
    });
  }

  addSettlement() {
    const settlement: Settlement = {
      type: null,
      buc: '',
      adn: '',
      amount: '',
      erp: null,
      company_code: '',
      me: '',
      leid: '',
      sys_desc: '',
      cCodeSet: false,
      foreign_curr: ''
    };
    this.settlements.push(_.clone(settlement));
    this.cdr.detectChanges();
  }

  removeSettlement(index: number) {
    this.settlements.splice(index, 1);
    this.setSettleAmount();
    this.cdr.detectChanges();
  }

  setSettleAmount() {
    let num = 0;
    for (let i = 0; i < this.settlements.length; i++) {
      const amount = this.settlements[i].amount;
      if (amount && amount !== '') {
        num += parseFloat(amount.toString());
      }
    }
    this.settleAmount = (num - this.amountToSettle).toFixed(2);
  }

  onChange() {
    this.setSettleAmount();
    this.cdr.detectChanges();
  }

  clearSelection(settlement: Settlement) {
    settlement.cCodeSet = false;
    settlement.company_code = '';
    settlement.foreign_curr = '';
    this.cdr.detectChanges();
  }

  setCompanyCode(settlement: Settlement) {
    if (settlement.company_code && settlement.company_code.CUR) {
      settlement.foreign_curr = settlement.company_code.CUR;
    }
    settlement.cCodeSet = true;
    this.cdr.detectChanges();
  }

  onCompanyCodeSelect(settlement: Settlement, item: any) {
    settlement.company_code = item;
    this.setCompanyCode(settlement);
  }

  searchCompanyCodes(searchString: string) {
    if (!searchString || searchString.length < 2) {
      this.companyCodes = [];
      this.noResults = false;
      return;
    }

    this.loadingCompanyCodes = true;
    this.noResults = false;

    this.methodSettlementService.getCompanyCodes(
      this.GENERAL_CONFIG.base_url,
      searchString,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.loadingCompanyCodes = false;
        if (data.errorMessage && data.errorMessage.length > 0 && data.errorMessage !== 'null') {
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          if (data.jsonObject && data.jsonObject.length) {
            this.companyCodes = data.jsonObject;
            this.noResults = false;
          } else {
            this.companyCodes = [];
            this.noResults = true;
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.loadingCompanyCodes = false;
        console.error('Error searching company codes:', error);
        this.cdr.detectChanges();
      }
    );
  }

  validateBucAdn(index: number) {
    const settlement = this.settlements[index];

    if (!settlement.buc || settlement.buc.length === 0 || !settlement.adn || settlement.adn.length === 0) {
      return;
    }

    settlement.valid = 'loading';
    this.cdr.detectChanges();

    this.methodSettlementService.validateBucAdn(
      this.GENERAL_CONFIG.base_url,
      settlement.buc,
      settlement.adn,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        if (result.callSuccess === '1') {
          settlement.valid = true;
        } else {
          settlement.valid = false;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        settlement.valid = false;
        this.cdr.detectChanges();
      }
    );
  }

  deleteSettlement() {
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we process this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    this.crudLoading = true;
    this.cdr.detectChanges();

    this.methodSettlementService.deleteSettlement(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.crudLoading = false;
        if (data.errorMessage && data.errorMessage.length > 0 && data.errorMessage !== 'null') {
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Method of Settlement was deleted', 'Close', {
            duration: 3000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this._snackBar.open('Error deleting settlement', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  save() {
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    // Basic validation
    for (let i = 0; i < this.settlements.length; i++) {
      const settlement = this.settlements[i];
      if (!settlement.type) {
        this._snackBar.open('Please select a Settlement Type for all entries', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
        return;
      }
      if (settlement.type.value === 'IBS') {
        if (!settlement.buc || !settlement.adn) {
          this._snackBar.open('Please enter BUC and ADN codes for IBS settlements', 'Close', {
            duration: 4000,
            panelClass: ['warning-snackbar']
          });
          return;
        }
      } else {
        if (!settlement.erp) {
          this._snackBar.open('Please select an ERP for ACA settlements', 'Close', {
            duration: 4000,
            panelClass: ['warning-snackbar']
          });
          return;
        }
      }
      if (!settlement.amount || settlement.amount === '') {
        this._snackBar.open('Please enter an amount for all settlements', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
        return;
      }
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const returnObj = {
      leid: this.rowData.LEID,
      division: this.rowData.Division,
      object_id: this.rowData.object_id,
      message: 'Method Of Settlement was updated'
    };

    // Prepare settlement array for save
    const settlementArray = _.cloneDeep(this.settlements);

    settlementArray.forEach((object: any) => {
      object.type = object.type ? object.type.value : '';
      object.buc = (object.type === 'IBS') ? object.buc : '';
      object.adn = (object.type === 'IBS') ? object.adn : '';
      object.company_code = (object.type !== 'IBS' && object.erp && object.erp.value === 'CCL')
        ? (object.company_code && object.company_code.NAME ? object.company_code.NAME : '')
        : '';
      object.leid = (object.type !== 'IBS' && object.erp && object.erp.value !== 'CCL') ? object.leid : '';
      object.me = (object.type !== 'IBS' && object.erp && object.erp.value !== 'CCL') ? object.me : '';
      object.erp = (object.type !== 'IBS' && object.erp) ? object.erp.value : '';
      object.sys_desc = (object.erp === 'Other') ? object.sys_desc : '';
    });

    this.methodSettlementService.saveSettlementArray(
      this.GENERAL_CONFIG.base_url,
      returnObj,
      settlementArray,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (result.callSuccess !== '1') {
          this._snackBar.open(result.errorMessage || 'Error saving data', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Method Of Settlement was updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
}
